import { useEffect, useState } from "react";
import {
    ReactionActionState,
    useReactionActionStore,
} from "../../Presentation/Components/ReactionsTooltip/ReactionActionState";
import { isDeviceOutputDesktop } from "../../Domain/DeviceOutputState";

export enum TOOLTIP_STATES {
    OPEN,
    CLOSE,
}

const DEFAULT_CLOSING_DELAY = 300;

export const useTooltipStateHandler = (
    callbackFn: (value: boolean) => void
) => {
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();
    const delayInOpeningTheTooltip = isDeviceOutputDesktop() ? 250 : 0;
    const { reactionActionState } = useReactionActionStore();

    useEffect(() => {
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [timeoutId]);

    function tooltipStateHandler(
        state: TOOLTIP_STATES,
        closingDelay = DEFAULT_CLOSING_DELAY as number,
        openingDelay = delayInOpeningTheTooltip as number
    ) {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        if (
            state === TOOLTIP_STATES.OPEN &&
            reactionActionState === ReactionActionState.IDLE
        ) {
            setTimeoutId(
                setTimeout(() => {
                    callbackFn(true);
                }, openingDelay)
            );
        } else {
            setTimeoutId(
                setTimeout(() => {
                    callbackFn(false);
                }, closingDelay)
            );
        }
    }

    return { tooltipStateHandler, reactionActionState };
};

export const useSelfClosingTooltipStateHandler = (
    callbackFn: (value: boolean) => void
) => {
    const [openingTimeoutId, setOpeningTimeoutId] =
        useState<NodeJS.Timeout | null>(null);
    const [closingTimeoutId, setClosingTimeoutId] =
        useState<NodeJS.Timeout | null>(null);

    return (state: TOOLTIP_STATES, openingTime = 0, closingTime = 1500) => {
        if (closingTimeoutId) {
            clearTimeout(closingTimeoutId);
        }
        if (openingTimeoutId) {
            clearTimeout(openingTimeoutId);
        }
        if (state === TOOLTIP_STATES.OPEN) {
            setOpeningTimeoutId(
                setTimeout(() => {
                    callbackFn(true);
                }, Math.max(0, openingTime))
            );
            if (closingTime !== -1) {
                setClosingTimeoutId(
                    setTimeout(() => {
                        callbackFn(false);
                    }, Math.max(0, openingTime + closingTime))
                );
            }
        } else {
            setClosingTimeoutId(
                setTimeout(() => {
                    callbackFn(false);
                }, closingTime)
            );
        }
    };
};
